import { ReactNode } from 'react';
import { Avatar, Box, Paper, Typography } from '@mui/material';
import { Storefront } from '@mui/icons-material';

interface Props {
  pills?: ReactNode;
  title: string;
  chips?: ReactNode;
  stats?: ReactNode;
  actions?: ReactNode;
}

export function ProductHero({ pills, title, chips, stats, actions }: Props) {
  return (
    <Paper
      elevation={1}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        color: 'primary.contrastText',
        px: { xs: 2.5, sm: 3, md: 4 },
        py: { xs: 2.5, sm: 3, md: 3.5 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: -80,
          top: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: (theme) =>
            `radial-gradient(circle, ${theme.alpha(theme.palette.common.white, 0.08)} 0%, ${theme.alpha(theme.palette.common.white, 0)} 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', gap: { xs: 2, sm: 3 }, alignItems: 'center' }}>
        <Avatar
          sx={{
            width: { xs: 56, sm: 72, md: 88 },
            height: { xs: 56, sm: 72, md: 88 },
            flexShrink: 0,
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
          }}
        >
          <Storefront sx={{ fontSize: { xs: 28, sm: 36, md: 44 } }} />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {pills && <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>{pills}</Box>}
          <Typography component="h1" sx={{ typography: { xs: 'h5', md: 'h4' }, wordBreak: 'break-word' }}>
            {title}
          </Typography>
          {chips && (
            <Box
              sx={{
                mt: 0.75,
                display: 'flex',
                gap: { xs: 1, sm: 2.25 },
                flexWrap: 'wrap',
                alignItems: 'center',
                typography: 'body2',
                color: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.85),
                minWidth: 0,
              }}
            >
              {chips}
            </Box>
          )}
        </Box>
      </Box>

      {stats && (
        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 2, md: 3 },
            mt: { xs: 2, md: 2.75 },
            pt: { xs: 2, md: 2.5 },
            borderTop: 1,
            borderColor: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.18),
          }}
        >
          {stats}
        </Box>
      )}

      {actions && (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            gap: 1.25,
            flexWrap: 'wrap',
            mt: { xs: 2, md: 2.5 },
            pt: { xs: 2, md: 2.5 },
            borderTop: 1,
            borderColor: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.18),
          }}
        >
          {actions}
        </Box>
      )}
    </Paper>
  );
}

export const detailsPageSx = {
  px: { xs: 1.5, sm: 2.5, md: 4 },
  pt: { xs: 1.5, sm: 2.5, md: 3 },
  pb: { xs: 6, sm: 8, md: 10 },
  maxWidth: 1280,
  mx: 'auto',
  boxSizing: 'border-box',
} as const;

export const detailsGridSx = {
  mt: { xs: 1.5, sm: 2.5 },
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 380px) 1fr' },
  gap: { xs: 1.5, sm: 2.5 },
  alignItems: 'start',
} as const;
